import { useEffect, useState } from 'react'
import { Diamond } from 'lucide-react'
import { HIGHLIGHT_TIMING, ratingTier, type CutinTier } from '@/lib/ai-result-tier'
import { RATING_COLORS } from '@/lib/rating-colors'
import { playHighlightHit, vibrate } from '@/lib/sfx'
import type { AnalyzeResultItem } from '@/api/achievements'

/** カードの枠の色（カットインの段階と揃える）。Tailwind JIT が検出できるよう静的リテラルで持つ。 */
const FRAME_STYLES: Record<CutinTier, string> = {
  red: 'bg-red-500',
  gold: 'bg-amber-300',
  rainbow: 'cutin-rainbow-band',
}

/**
 * カットイン直後に「どの実績が高評価だったか」を1件ずつ叩きつけて見せる。
 * 親がアンマウントすればタイマーは止まる（音はスキップ時に親が止める）。
 */
export default function AiHighlight({ items, rest }: { items: AnalyzeResultItem[]; rest: number }) {
  const [visible, setVisible] = useState(0)
  // 「ほか n件」も1枠として叩きつける
  const slots = items.length + (rest > 0 ? 1 : 0)

  useEffect(() => {
    const timers = Array.from({ length: slots }, (_, i) =>
      setTimeout(() => {
        setVisible(i + 1)
        playHighlightHit(i)
        vibrate(40)
      }, i * HIGHLIGHT_TIMING.CARD_MS),
    )
    // ジャジャーンは判定完了に進んでも鳴らし切る（スキップ時は親の skip() が全停止する）
    return () => timers.forEach(clearTimeout)
  }, [slots])

  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-6" aria-hidden>
      <p className="text-xs font-bold tracking-[.3em] text-white/60">HIGH SCORE</p>
      {items.slice(0, visible).map((item) => {
        const tier = ratingTier(item.ai_rating) ?? 'red'
        const diamonds = Math.max(0, (item.ai_rating ?? 0) - 5)
        return (
          <div
            key={item.id}
            className={`highlight-slam w-full max-w-xs rounded-lg p-[2px] md:max-w-sm ${FRAME_STYLES[tier]}`}
          >
            <div className="flex items-center gap-3 rounded-[6px] bg-black/90 px-3 py-2.5">
              <p className="line-clamp-2 flex-1 text-sm font-bold text-white">{item.title}</p>
              <div className="flex shrink-0 flex-col items-end gap-0.5">
                <span className="flex gap-0.5">
                  {Array.from({ length: diamonds }, (_, n) => (
                    <Diamond key={n} size={14} className={RATING_COLORS.aiDiamond} />
                  ))}
                </span>
                <span className="text-xs font-bold text-amber-300">+{item.earned_points}</span>
              </div>
            </div>
          </div>
        )
      })}
      {rest > 0 && visible > items.length && (
        <p className="highlight-slam text-sm font-bold text-white/80">ほか {rest}件</p>
      )}
    </div>
  )
}
